import React, { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { useGlobalContext } from "context/globalContext";
import { IoMdClose } from "react-icons/io";
import { createOrUpdateConfigAPI, getConfigCategoryAPI } from "Api/config";

const ConfigForm = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  initialFormData,
  
}) => {
const {
  setFetchConfig,
  formData,
  setFormData,
  initialFormState,
  configCategories,
  setConfigCategories,
} = useGlobalContext();

  const [originalFormData, setOriginalFormData] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");


useEffect(() => {
  const fetchCategories = async () => {
    if (configCategories.length > 0) return; 
    const token = localStorage.getItem("jwtToken");
    const response = await getConfigCategoryAPI(token);

    if (Array.isArray(response)) {
      setConfigCategories(response);
    } else if (response?.data && Array.isArray(response.data)) {
      setConfigCategories(response.data);
    } else {
      toast.error("Failed to load config categories.");
    }
  };

  fetchCategories();
}, [configCategories, setConfigCategories]);

  useEffect(() => {
    if (isEditMode && initialFormData) {
      setFormData(initialFormData);
      setOriginalFormData(initialFormData);
    } else {
      setFormData(initialFormState);
      setOriginalFormData(initialFormState);
    }
    setIsDirty(false);
  }, [isEditMode, initialFormData, initialFormState, setFormData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedForm = {
      ...formData,
      [name]: value,
    };
    setFormData(updatedForm);

    if (name === "key") {
      setErrorMessage("");
    }

    const hasChanged = Object.keys(updatedForm).some(
      (key) => updatedForm[key] !== originalFormData[key]
    );
    setIsDirty(hasChanged);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.height = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      ...formData,
      status: formData.status === "active" ? true : false,
    };
    const token = localStorage.getItem("jwtToken");

    const response = await createOrUpdateConfigAPI(
      payload,
      setIsSubmitting,
      token
    );

    if (
      response &&
      response?.statusCode !== 400 &&
      response?.message !== "Key already exists"
    ) {
      const successMessage = formData?.id
        ? "Config updated successfully"
        : "Config created successfully";
      toast.success(successMessage);

      onSubmit(response.data);
      onClose();
      setFormData(initialFormState);
      setFetchConfig(true);
    }

    if (
      !response &&
      response?.statusCode !== 400 &&
      response?.message !== "Key already exists"
    ) {
      toast.error("Error creating/updating config. Please try again.");
    }
  };

  const handleCancel = () => {
    setFormData(initialFormState);
    onClose();
  };

  const isFormValid = useMemo(() => {
    return (
      formData.key &&
      formData.value &&
      formData.status &&
      formData.configCategory &&
      !errorMessage &&
      isDirty
    );
  }, [formData, errorMessage, isDirty]);

  if (!isOpen) return null;

  return (
    <div className="bg-black fixed inset-0 z-50 flex h-screen items-center justify-center bg-opacity-50">
      <div className="relative z-50 mx-auto flex w-4/5 flex-col items-center justify-start rounded-xl bg-white p-6 shadow-xl dark:!bg-gray-10 dark:text-white ">
        <button
          type="button"
          onClick={handleCancel}
          className="absolute top-6 right-6 text-gray-500 hover:text-gray-600 focus:outline-none dark:hover:text-gray-100 "
        >
          <IoMdClose size={24} />
        </button>

        <form onSubmit={handleSubmit} className="w-full">
          <div className="mb-4 mt-4 flex justify-center gap-4">
            <div className="w-1/2">
              <label className="mb-2 block font-bold text-gray-700  dark:text-white ">
                <span className="text-red-600">*</span> Key:
              </label>
              <input
                type="text"
                name="key"
                autoComplete="off"
                value={formData.key}
                disabled={isEditMode}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white "
              />
              {errorMessage && (
                <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
              )}
            </div>
            <div className="w-1/2">
              <label className="mb-2 block font-bold text-gray-700 dark:text-white ">
                <span className="text-red-600">*</span> Config category:
              </label>
             <select
  name="configCategory"
  onChange={handleChange}
  value={formData.configCategory}
  className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white cursor-pointer"
>
  <option value="" disabled>
    Select Category
  </option>
  {configCategories.length > 0 ? (
    configCategories.map((category, idx) => (
     <option key={idx} value={category}>
  {category
    .replace(/([a-z])([A-Z])/g, "$1 $2") 
    .replace(/^\w/, (c) => c.toUpperCase())} 
</option>
    ))
  ) : (
    <option disabled>Loading categories...</option>
  )}
</select>

            </div>
          </div>

          <div className="mb-4 flex justify-center gap-4">
            <div className="w-1/2">
              <label className="mb-2 block font-bold text-gray-700 dark:text-white ">
                <span className="text-red-600">*</span> Status:
              </label>
              <div className="justify-right flex items-center">
                <label className="mr-4 dark:text-white ">
                  <input
                    type="radio"
                    name="status"
                    value="active"
                    checked={formData.status === "active"}
                    onChange={handleChange}
                    className="relative top-0.5 mr-1 cursor-pointer"
                  />
                  Active
                </label>
                <label className=" dark:text-white ">
                  <input
                    type="radio"
                    name="status"
                    value="inactive"
                    checked={formData.status === "inactive"}
                    onChange={handleChange}
                    className="relative top-0.5 mr-1 cursor-pointer"
                  />
                  In-Active
                </label>
              </div>
            </div>
            <div className="w-1/2">
              <label className="mb-2 block font-bold text-gray-700 dark:text-white ">
                Comment:
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white "
              ></textarea>
            </div>
          </div>

          <div className="mb-4 flex justify-center">
            <div className="w-full max-w-full">
              <label className="mb-2 block font-bold text-gray-700 dark:text-white ">
                <span className="text-red-600">*</span> Value:
              </label>
              <textarea
                id="value"
                name="value"
                value={formData.value || ""}
                onChange={handleChange}
                className="focus:border-transparent h-[30vh] w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white "
                disabled={isSubmitting}
                required
              ></textarea>
            </div>
          </div>

          <div className="mb-0 flex justify-end space-x-4">
            <button
              type="submit"
              className={`rounded-md py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
                isSubmitting || !isFormValid
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-green-600 hover:bg-green-500"
              }`}
              disabled={isSubmitting || !isFormValid}
            >
              {isSubmitting ? "Submitting..." : isEditMode ? "Update" : "Add"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md bg-red-600 py-2 px-4 text-white hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfigForm;
